import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as Ccs2Icon } from '../../../assets/device-hub/connecter-type/ccs2.svg';
import { ReactComponent as ChademoIcon } from '../../../assets/device-hub/connecter-type/CHAdeMO.svg';
import { ReactComponent as J1772Icon } from '../../../assets/device-hub/connecter-type/J1172.svg';
import { ReactComponent as NacsIcon } from '../../../assets/device-hub/connecter-type/Nacs.svg';
import { ReactComponent as Type2Icon } from '../../../assets/device-hub/connecter-type/type2.svg';
import { ReactComponent as ChargerIcon } from '../../../assets/icons/sidebar/sidebar-device.svg';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import deviceApi from '../../../utils/api/device-hub';

interface ConnectorSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
}

interface ConnectorRow {
  _id?: string;
  connectorType: string;
  status: string;
  currentType?: string;
  connectorFormat?: string;
  maxVoltage?: number | string;
  maxCurrent?: number | string;
  connectorRating?: any;
}

/** Maps connector type value to its SVG icon component */
const CONNECTOR_ICON_MAP: Record<string, React.FunctionComponent<React.SVGProps<SVGSVGElement>>> = {
  CCS1: Ccs2Icon,
  CCS2: Ccs2Icon,
  type2: Type2Icon,
  type1: Type2Icon,
  CHAdeMO: ChademoIcon,
  NACS: NacsIcon,
  J1772: J1772Icon,
  TYPE2_CCS2: Ccs2Icon,
};

/**
 * Returns the correct SVG icon for a connector type.
 * @param {any} param0 - The connector type string from the data.
 * @param {any} param0.connectorType - The connectorType prop
 * @returns {any} JSX element with the connector icon
 */
function ConnectorTypeIcon({ connectorType }: Readonly<{ connectorType: string }>) {
  const Icon = CONNECTOR_ICON_MAP[connectorType] || ChargerIcon;
  return <Icon width={36} height={36} />;
}

/**
 * Side panel for displaying connector information
 * @param {any} properties - The properties
 * @returns {any} ConnectorSidePanel component
 */
export default function ConnectorSidePanel(properties: Readonly<ConnectorSidePanelProperties>) {
  const { t } = useTranslation('deviceHub');
  const { fetchIdFrom, id } = properties;
  const [searchParameters] = useSearchParams();
  const dispatch = useAppDispatch();
  const [connecterData, setConnecterData] = useState<ConnectorRow[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /**
   * Fetches connector details for the specified connector ID and updates the component state.
   * 
   * Displays a loading indicator while the request is in progress, stores the
   * connector information on success, and shows an error notification if the
   * request fails.
   * @param {any} connecterId - Unique identifier of the connector to retrieve details for.
   * @returns {any} Promise that resolves when the connector data has been fetched and processed.
   */
  const getConnectorData = async (connecterId: string): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await deviceApi.fetchConnectorData(connecterId);
      setConnecterData(response.data.connectorInformation);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message:
            error?.response?.data?.message || error?.message || t('failedToFetchConnectors'),
          status: true,
          type: 'error',
          code: error?.response?.status,
        }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let connectorId: string | null | undefined;

    if (fetchIdFrom === 'queryParams') {
      connectorId = searchParameters.get('connectorDeviceId');
    } else {
      connectorId = id;
    }

    if (!connectorId) return;

    getConnectorData(connectorId);
  }, [fetchIdFrom, id, searchParameters]);

  if (isLoading) {
    return (
      <div className="loader">
        <BatteryLoader />
      </div>
    );
  }
  if (!connecterData || connecterData.length === 0) {
    return (
      <div className="connector-side-panel">
        <div className="csp-body">
          <p className="no-data-text">{t('noConnectorsFound')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="connector-side-panel">
      <div className="csp-body">
        {connecterData.map((c: ConnectorRow, index: number) => {
          const ratingObject = typeof c.connectorRating === 'object' ? c.connectorRating : null;
          const power = ratingObject
            ? `${ratingObject.capacityKW || ''} ${ratingObject.unit || 'kW'}`
            : '';
          const currentType = ratingObject?.currentType
            ? ratingObject.currentType.toUpperCase()
            : 'AC';
          const format = c.connectorFormat
            ? c.connectorFormat.charAt(0).toUpperCase() + c.connectorFormat.slice(1)
            : '-';
          // const connectorRateingName = ratingObject?.name || '';
          const connecterStatus = c.status;
          return (
            <div className="csp-card" key={index}>
              <div className="csp-card-top">
                <div className="csp-card-icon">
                  <ConnectorTypeIcon connectorType={c.connectorType} />
                </div>
                <div className="csp-card-title-area">
                  <h4>{c.connectorType || '-'}</h4>
                  <p>
                    {currentType}  {power}
                  </p>
                </div>
                <div
                  className={`csp-card-badge ${connecterStatus ? connecterStatus.toLowerCase() : 'unknown'}`}>
                  {connecterStatus || t('unknown')}
                </div>
              </div>
              <div className="csp-card-divider" />
              <div className="csp-card-bottom">
                <div className="csp-col">
                  <p className="csp-label">{t('connectorFormat')}</p>
                  <p className="csp-value">{format}</p>
                </div>
                <div className="csp-col">
                  <p className="csp-label">{t('maxVoltage')}</p>
                  <p className="csp-value">{c.maxVoltage ? `${c.maxVoltage} V` : '-'}</p>
                </div>
                <div className="csp-col">
                  <p className="csp-label">{t('maxCurrent')}</p>
                  <p className="csp-value">{c.maxCurrent ? `${c.maxCurrent} kW` : '-'}</p>
                </div>
                <div className="csp-col">
                  <p className="csp-label">{t('currentType')}</p>
                  <p className="csp-value">{c.currentType}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
